/**
 * 챌린지 서비스 — 내부만 QBase 직결로 교체 (LiveSQL 테넌트 편입 1단계, 2026-08-19)
 *
 * 내보내는 함수 이름·인자·반환 봉투는 그대로다. 바뀐 것은 "누가 호출자인가" 하나뿐이다:
 *   before: 브라우저 → Node(:3003) /api/challenge/* → 서비스계정(admin1) → QBase
 *   after : 브라우저 → QBase (본인 세션)
 *
 * 그래서 challengeActions.js·QueryChallenge.js·ParticipationTab.js 는 손대지 않는다.
 * 검토서: qbase/docs/architecture/LIVESQL_TENANT_MIGRATION_REVIEW.md
 */

import {
  listChallenges,
  listQuiz,
  listParticipation,
  listInvitedChallenges,
  saveParticipation,
} from './livesqlRepo';
import i18n from '../i18n';

export const fetchChallengeList = async () => {
  return listChallenges();
};

export const fetchInvitedChallenges = async (groupId) => {
  return listInvitedChallenges(groupId);
};

export const fetchChallengeQuizList = async (challengeId) => {
  return listQuiz(challengeId);
};

export const fetchChallengeQuizParticipation = async (challengeId, userId) => {
  return listParticipation(challengeId, userId);
};

/**
 * 쿼리 챌린지 퀴즈 참여 저장
 *
 * @param {Object} participationData
 * @param {string} participationData.저장구분 - "신규", "수정", "삭제"
 * @param {string} participationData.challengeId - 챌린지 ID
 * @param {string} participationData.quizId - 퀴즈 ID (QUIZ_NO)
 * @param {string} participationData.groupId - 참여그룹 ID (비공개 챌린지만 필수)
 * @param {string} participationData.publicYn - 공개여부 ('Y': 공개, 'N': 비공개)
 * @param {string} participationData.ptcpSql - 참여 SQL
 * @param {string} participationData.successYn - 성공 여부
 *
 * ★ usrid 는 더 이상 필수가 아니다 — 제출자는 서버가 세션에서 정한다.
 *   본문에 실려 가더라도 무시되므로, 여기서 검증할 이유가 없어졌다.
 *   (예전에는 서버가 보는 호출자가 서비스계정 하나뿐이라 본문 값 말고는 제출자를 알 수 없었다)
 */
export const saveQuizParticipation = async (participationData) => {
  try {
    const requiredFields = ['저장구분', 'challengeId', 'quizId', 'successYn'];
    for (const field of requiredFields) {
      if (!participationData[field]) {
        throw new Error(i18n.t('challenge:svc.missingParam', { field: field }));
      }
    }

    // 비공개 챌린지인 경우 groupId 필수
    if (participationData.publicYn === 'N' && !participationData.groupId) {
      throw new Error(i18n.t('challenge:svc.groupRequired'));
    }

    const validSaveTypes = ['신규', '수정', '삭제'];
    if (!validSaveTypes.includes(participationData.저장구분)) {
      throw new Error(i18n.t('challenge:svc.badSaveType', { allowed: validSaveTypes.join(', ') }));
    }

    return await saveParticipation(participationData);
  } catch (error) {
    console.error('퀴즈 참여 저장 실패:', error);
    // QbaseError 는 status 를 들고 온다 — 401(로그인 만료)과 500(서버 오류)을 구분해서 전한다
    if (error.status === 401 || error.status === 403) {
      throw new Error(i18n.t('challenge:svc.needLogin'));
    }
    throw error;
  }
};

/**
 * 퀴즈 참여 저장 헬퍼 함수들
 *
 * userId 인자는 호출부 호환을 위해 남겨 두었지만 서버는 세션 값을 쓴다.
 */

// 신규 참여 저장
export const saveNewParticipation = async (challengeId, quizId, userId, groupId, sql, isCorrect, score = 0, publicYn = 'Y', failRsn = null) => {
  return await saveQuizParticipation({
    저장구분: '신규',
    challengeId,
    quizId,
    groupId: groupId || '',
    publicYn,
    ptcpSql: sql,
    ptcpScd: isCorrect ? '정답' : '오답',
    ptcpScore: score,
    successYn: isCorrect ? 'Y' : 'N',
    // 튜닝형이 채점 수치를 JSON 으로 싣는 칸. 001·002 는 비워 둔다.
    failRsn,
  });
};

// 참여 정보 수정
export const updateParticipation = async (challengeId, quizId, userId, groupId, sql, isCorrect, score = 0, publicYn = 'Y', failRsn = null) => {
  return await saveQuizParticipation({
    저장구분: '수정',
    challengeId,
    quizId,
    groupId: groupId || '',
    publicYn,
    ptcpSql: sql,
    ptcpScd: isCorrect ? '정답' : '오답',
    ptcpScore: score,
    successYn: isCorrect ? 'Y' : 'N',
    // 튜닝형이 채점 수치를 JSON 으로 싣는 칸. 001·002 는 비워 둔다.
    failRsn,
  });
};

// 참여 정보 삭제
export const deleteParticipation = async (challengeId, quizId, userId, groupId, publicYn = 'Y') => {
  return await saveQuizParticipation({
    op: 'delete',   // 로컬 저장소 분기가 이것으로 삭제를 가른다(livesqlRepo) — QBase 로는 빼고 보낸다
    저장구분: '삭제',
    challengeId,
    quizId,
    groupId: groupId || '',
    publicYn,
    successYn: 'N',
  });
};
